import { useState } from 'react';

/**
 * A hook for managing a value stored in local storage.
 *
 * @template InitialValue - The generic type of the value to be stored.
 * @param {string} key - The key to store the value in local storage.
 * @param {InitialValue} initialValue - The initial value for the storage.
 * @returns {[InitialValue, (value: InitialValue | ((prevValue: InitialValue) => InitialValue)) => void]} - An array with the current value and a function to update it.
 */

function useLocalStorage<InitialValue>(
  key: string,
  initialValue: InitialValue,
): [InitialValue, (value: InitialValue) => void] {
  // Pass initial state function to useState so logic is only executed once
  const [storedValue, setStoredValue] = useState<InitialValue>(() => {
    try {
      const item = window.localStorage.getItem(key);
      return item ? JSON.parse(item) : initialValue;
    } catch (error) {
      return initialValue;
    }
  });

  const setValue = (
    value: InitialValue | ((prevValue: InitialValue) => InitialValue),
  ) => {
    try {
      const valueToStore =
        value instanceof Function ? value(storedValue) : value;
      setStoredValue(valueToStore);
      window.localStorage.setItem(key, JSON.stringify(valueToStore));
      // eslint-disable-next-line no-empty
    } catch (error) {}
  };

  return [storedValue, setValue];
}

export default useLocalStorage;
